/**
 * Creating and editing promo codes — 14 Oct 2026.
 *
 * `PromoCodes` lives in the product project, which the CRM only reads. So the
 * writes go to skida-backend (`POST /admin/promo-codes`, `PATCH
 * /admin/promo-codes/{code}`), signed with the product-project session the CRM
 * already holds for its product columns. The backend checks the caller, writes
 * every field the app's redeem path needs, and refuses to change a code once
 * it exists. See PROMO_CODE_API.md in skida-backend.
 */
import { productAuth } from '@/shared/firebase/client'

const API =
  import.meta.env.VITE_PRODUCT_API_URL || 'https://fastapi-app-569215357618.europe-west1.run.app'

/** The two paywall offerings a code can unlock. */
export const OFFERINGS = [
  { id: 'premium-promo-20', label: '20 % off' },
  { id: 'premium-promo-50', label: '50 % off' },
] as const

export type OfferingId = (typeof OFFERINGS)[number]['id']

export const offeringLabel = (id: string) => OFFERINGS.find((o) => o.id === id)?.label ?? id

/** Where a code is redeemed: the web app's map, which opens the paywall. */
export const promoUrl = (code: string) => `https://app.skida.app/map/?code=${encodeURIComponent(code)}`

export interface PromoCodeFields {
  offering: OfferingId
  partner: string
  limit: number
  active: boolean
  durationDays: number | null
  expiresAt: string | null
}

async function call<T>(method: 'POST' | 'PATCH', path: string, body: unknown): Promise<T> {
  const user = productAuth.currentUser
  if (!user) {
    throw new Error('Connect the Skida App account first — the banner at the top of the page.')
  }
  let res: Response
  try {
    res = await fetch(`${API}${path}`, {
      method,
      headers: { Authorization: `Bearer ${await user.getIdToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    // A refused CORS preflight lands here too, and looks the same.
    throw new Error(
      'Could not reach the Skida App backend. The promo code admin API (skida-backend PR #59) may not be deployed yet.',
    )
  }
  if (res.status === 404 || res.status === 405) {
    const text = await res.clone().text()
    if (/Not Found|Method Not Allowed/.test(text)) {
      throw new Error('The Skida App backend does not have the promo code admin API yet (skida-backend PR #59).')
    }
  }
  const out = (await res.json().catch(() => ({}))) as { message?: string; detail?: unknown }
  if (!res.ok) {
    const detail = typeof out.detail === 'string' ? out.detail : null
    throw new Error(out.message ?? detail ?? `The backend answered ${res.status}.`)
  }
  return out as T
}

export async function createPromoCode(code: string, fields: PromoCodeFields): Promise<void> {
  await call('POST', '/admin/promo-codes', {
    code,
    offering: fields.offering,
    partner: fields.partner,
    limit: fields.limit,
    active: fields.active,
    durationDays: fields.durationDays,
    expiresAt: fields.expiresAt,
  })
}

export async function updatePromoCode(code: string, fields: PromoCodeFields): Promise<void> {
  await call('PATCH', `/admin/promo-codes/${encodeURIComponent(code)}`, {
    offering: fields.offering,
    partner: fields.partner,
    limit: fields.limit,
    active: fields.active,
    durationDays: fields.durationDays,
    expiresAt: fields.expiresAt,
    clearDurationDays: fields.durationDays === null,
    clearExpiresAt: fields.expiresAt === null,
  })
}

/** What the backend accepts as a code, applied as the person types. */
export const normaliseCode = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 32)
